import React, { useEffect, useState, useCallback } from "react";
import {
  Users,
  CalendarDays,
  Trophy,
  Check,
  MapPin,
  Clock,
  Flame,
  Dumbbell,
  Activity,
  Sparkles,
  Newspaper,
  UtensilsCrossed,
  ArrowRight,
  X,
  Plus,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import CommunityCreateModal from "@/components/app/CommunityCreateModal";

const TABS = [
  { id: "groups", label: "Groups", short: "Groups", icon: Users, creatable: true },
  { id: "events", label: "Events", short: "Events", icon: CalendarDays, creatable: true },
  { id: "challenges", label: "Challenges", short: "PRs", icon: Trophy, creatable: true },
  { id: "stories", label: "Stories", short: "Stories", icon: Newspaper, creatable: false },
  { id: "recipes", label: "Recipes", short: "Recipes", icon: UtensilsCrossed, creatable: false },
];

const ICONS = { Flame, Dumbbell, Activity, Sparkles };

const CREATE_BTN_LABEL = {
  groups: "New Crew",
  events: "Host Event",
  challenges: "New Challenge",
};

const renderTabContent = ({ tab, data, toggle, openStory, openRecipe }) => {
  if (tab === "groups") return <Groups items={data.groups} onToggle={(id) => toggle("groups", id)} />;
  if (tab === "events") return <Events items={data.events} onToggle={(id) => toggle("events", id)} />;
  if (tab === "challenges")
    return <Challenges items={data.challenges} onToggle={(id) => toggle("challenges", id)} />;
  if (tab === "stories") return <Stories items={data.stories} onOpen={openStory} />;
  return <Recipes items={data.recipes} onOpen={openRecipe} />;
};

const Community = () => {
  const [tab, setTab] = useState("groups");
  const [data, setData] = useState({
    groups: [],
    events: [],
    challenges: [],
    stories: [],
    recipes: [],
  });
  const [loading, setLoading] = useState(true);
  const [reading, setReading] = useState(null); // {kind, item}
  const [creating, setCreating] = useState(null); // 'groups' | 'events' | 'challenges'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [g, e, c, s, r] = await Promise.all([
        api.get("/community/groups"),
        api.get("/community/events"),
        api.get("/community/challenges"),
        api.get("/community/stories"),
        api.get("/community/recipes"),
      ]);
      setData({
        groups: g.data,
        events: e.data,
        challenges: c.data,
        stories: s.data,
        recipes: r.data,
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (kind, id) => {
    const PATHS = {
      groups: `/community/groups/${id}/join`,
      events: `/community/events/${id}/rsvp`,
      challenges: `/community/challenges/${id}/join`,
    };
    try {
      await api.post(PATHS[kind]);
      await load();
    } catch (err) {
      void err;
      toast.error("Couldn't update. Try again.");
    }
  };

  const openStory = async (id) => {
    try {
      const { data } = await api.get(`/community/stories/${id}`);
      setReading({ kind: "story", item: data });
    } catch (err) {
      void err;
      toast.error("Couldn't open story.");
    }
  };

  const openRecipe = async (id) => {
    try {
      const { data } = await api.get(`/community/recipes/${id}`);
      setReading({ kind: "recipe", item: data });
    } catch (err) {
      void err;
      toast.error("Couldn't open recipe.");
    }
  };

  return (
    <div data-testid="community-page">
      <p className="text-xs uppercase tracking-[0.3em] text-[#C6FF00] font-semibold mb-1">
        Crew
      </p>
      <h1 className="font-heading text-4xl md:text-5xl mb-6">Train Together.</h1>

      {/* Tabs — fit all 5 across without scrolling */}
      <div className="grid grid-cols-5 gap-1.5 mb-5" data-testid="community-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              data-testid={`community-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-1 sm:px-3 py-2.5 rounded-xl text-[9px] sm:text-[11px] uppercase tracking-[0.15em] sm:tracking-[0.2em] font-bold border transition-all ${
                active
                  ? "bg-[#C6FF00] text-[#0A0A0A] border-[#C6FF00]"
                  : "bg-transparent text-neutral-300 border-white/15 hover:border-[#C6FF00]"
              }`}
            >
              <Icon size={14} className="flex-shrink-0" />
              <span className="leading-none">
                <span className="sm:hidden">{t.short}</span>
                <span className="hidden sm:inline">{t.label}</span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Create button (only on creatable tabs) */}
      {TABS.find((t) => t.id === tab)?.creatable && (
        <div className="mb-5 flex justify-end">
          <button
            onClick={() => setCreating(tab)}
            data-testid={`crew-create-${tab}-btn`}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#C6FF00]/40 text-[#C6FF00] text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#C6FF00]/10 transition-colors"
          >
            <Plus size={12} /> {CREATE_BTN_LABEL[tab]}
          </button>
        </div>
      )}

      {loading ? (
        <div className="space-y-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-28 bg-[#111] rounded-2xl border border-white/5 animate-pulse" />
          ))}
        </div>
      ) : (
        renderTabContent({ tab, data, toggle, openStory, openRecipe })
      )}

      {reading && (
        <ContentDrawer item={reading.item} kind={reading.kind} onClose={() => setReading(null)} />
      )}

      {creating && (
        <CommunityCreateModal
          kind={creating}
          onClose={() => setCreating(null)}
          onCreated={() => {
            const created = creating;
            setCreating(null);
            load();
            toast.success(
              created === "groups"
                ? "Crew created."
                : created === "events"
                ? "Event live."
                : "Challenge launched."
            );
          }}
        />
      )}
    </div>
  );
};

const Groups = ({ items, onToggle }) =>
  items.length === 0 ? (
    <Empty label="No groups yet." />
  ) : (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {items.map((g) => (
        <div
          key={g.id}
          data-testid={`group-card-${g.id}`}
          className="bg-[#111] border border-white/5 rounded-2xl overflow-hidden hover:border-[#C6FF00]/40 transition-colors"
        >
          <div className="relative aspect-[16/9] bg-neutral-900">
            <img src={g.cover} alt={g.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
            <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-[#C6FF00] text-[#0A0A0A] text-[9px] font-bold uppercase tracking-wider">
              {g.discipline}
            </span>
          </div>
          <div className="p-5">
            <h3 className="font-heading text-2xl text-white tracking-wide mb-1">{g.name}</h3>
            <p className="text-xs text-neutral-400 line-clamp-2 mb-3">{g.description}</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-neutral-500">
                <span className="flex items-center gap-1">
                  <Users size={11} /> {g.members}
                </span>
                {g.location_city && (
                  <span className="flex items-center gap-1">
                    <MapPin size={11} /> {g.location_city}
                  </span>
                )}
              </div>
              <button
                data-testid={`group-toggle-${g.id}`}
                onClick={() => onToggle(g.id)}
                className={`px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all ${
                  g.joined
                    ? "bg-[#C6FF00]/20 text-[#C6FF00] border border-[#C6FF00]/40"
                    : "bg-[#C6FF00] text-[#0A0A0A]"
                }`}
              >
                {g.joined ? "Joined" : "Join"}
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

const Events = ({ items, onToggle }) =>
  items.length === 0 ? (
    <Empty label="No events scheduled." />
  ) : (
    <div className="space-y-4">
      {items.map((e) => {
        const dt = new Date(e.starts_at);
        return (
          <div
            key={e.id}
            data-testid={`event-card-${e.id}`}
            className="flex flex-col sm:flex-row gap-4 bg-[#111] border border-white/5 rounded-2xl overflow-hidden hover:border-[#C6FF00]/40 transition-colors"
          >
            <div className="sm:w-44 aspect-[16/9] sm:aspect-auto relative flex-shrink-0">
              <img src={e.cover} alt={e.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0A0A0A]/40" />
            </div>
            <div className="flex-1 p-4 sm:p-5 flex flex-col justify-between">
              <div>
                <h3 className="font-heading text-2xl text-white tracking-wide mb-1">{e.title}</h3>
                <p className="text-xs text-neutral-400 line-clamp-2">{e.description}</p>
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-widest text-neutral-500">
                  <span className="flex items-center gap-1">
                    <Clock size={11} />
                    {dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                    {" · "}
                    {dt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                  </span>
                  <span className="flex items-center gap-1">
                    <MapPin size={11} /> {e.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users size={11} /> {e.attendees}
                  </span>
                </div>
                <button
                  data-testid={`event-toggle-${e.id}`}
                  onClick={() => onToggle(e.id)}
                  className={`px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all ${
                    e.rsvp
                      ? "bg-[#C6FF00]/20 text-[#C6FF00] border border-[#C6FF00]/40 inline-flex items-center gap-1"
                      : "bg-[#C6FF00] text-[#0A0A0A]"
                  }`}
                >
                  {e.rsvp ? (
                    <>
                      <Check size={10} /> Going
                    </>
                  ) : (
                    "RSVP"
                  )}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

const Challenges = ({ items, onToggle }) =>
  items.length === 0 ? (
    <Empty label="No challenges live." />
  ) : (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {items.map((c) => {
        const Icon = ICONS[c.icon] || Trophy;
        return (
          <div
            key={c.id}
            data-testid={`challenge-card-${c.id}`}
            className="bg-[#111] border border-white/5 rounded-2xl p-5 hover:border-[#C6FF00]/40 transition-colors"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#C6FF00]/10 border border-[#C6FF00]/30 flex items-center justify-center">
                <Icon className="text-[#C6FF00]" size={20} />
              </div>
              <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-semibold border border-white/10 rounded-full px-2.5 py-1">
                {c.duration_days}d
              </span>
            </div>
            <h3 className="font-heading text-2xl text-white tracking-wide mb-1">{c.title}</h3>
            <p className="text-xs text-neutral-400 line-clamp-2 mb-3">{c.description}</p>
            <div className="flex items-center justify-between mt-2">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-1">
                <Users size={11} /> {c.participants} in
              </div>
              <button
                data-testid={`challenge-toggle-${c.id}`}
                onClick={() => onToggle(c.id)}
                className={`px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all ${
                  c.joined
                    ? "bg-[#C6FF00]/20 text-[#C6FF00] border border-[#C6FF00]/40 inline-flex items-center gap-1"
                    : "bg-[#C6FF00] text-[#0A0A0A]"
                }`}
              >
                {c.joined ? (
                  <>
                    <Check size={10} /> In
                  </>
                ) : (
                  "Join"
                )}
              </button>
            </div>
            {c.reward && (
              <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-[#C6FF00]/80 font-semibold">
                Reward · {c.reward}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

const Stories = ({ items, onOpen }) =>
  items.length === 0 ? (
    <Empty label="No stories yet." />
  ) : (
    <div className="space-y-4">
      {items.map((s, i) => {
        const featured = i === 0;
        return (
          <button
            key={s.id}
            data-testid={`story-card-${s.id}`}
            onClick={() => onOpen(s.id)}
            className={`text-left w-full block bg-[#111] border border-white/5 rounded-2xl overflow-hidden hover:border-[#C6FF00]/40 transition-colors ${
              featured ? "" : "sm:flex sm:items-stretch"
            }`}
          >
            <div
              className={`relative ${
                featured ? "aspect-[16/8]" : "sm:w-56 aspect-[16/10] sm:aspect-auto"
              } flex-shrink-0`}
            >
              <img src={s.cover} alt={s.title} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/80 via-transparent to-transparent" />
              <span className="absolute top-3 left-3 px-2 py-1 rounded-full bg-[#C6FF00] text-[#0A0A0A] text-[9px] font-bold uppercase tracking-wider">
                {s.category}
              </span>
            </div>
            <div className="p-5 flex-1">
              <h3 className="font-heading text-2xl md:text-3xl text-white tracking-wide leading-tight mb-2">
                {s.title}
              </h3>
              <p className="text-xs text-neutral-400 line-clamp-2 mb-3">{s.excerpt}</p>
              <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-neutral-500">
                <span className="flex items-center gap-1">
                  <Clock size={11} /> {s.read_minutes} min read
                </span>
                <span>{new Date(s.published_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                <ArrowRight size={11} className="ml-auto text-[#C6FF00]" />
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );

const Recipes = ({ items, onOpen }) =>
  items.length === 0 ? (
    <Empty label="No recipes yet." />
  ) : (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {items.map((r) => (
        <button
          key={r.id}
          data-testid={`recipe-card-${r.id}`}
          onClick={() => onOpen(r.id)}
          className="text-left bg-[#111] border border-white/5 rounded-2xl overflow-hidden hover:border-[#C6FF00]/40 transition-colors"
        >
          <div className="relative aspect-[16/10]">
            <img src={r.cover} alt={r.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/80 via-transparent to-transparent" />
            <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
              {(r.tags || []).slice(0, 2).map((t) => (
                <span
                  key={t}
                  className="px-2 py-0.5 rounded-full bg-[#C6FF00] text-[#0A0A0A] text-[9px] font-bold uppercase tracking-wider"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
          <div className="p-5">
            <h3 className="font-heading text-2xl text-white tracking-wide leading-tight mb-1">
              {r.title}
            </h3>
            <p className="text-xs text-neutral-400 mb-3">{r.tagline}</p>
            <div className="flex items-center gap-4 text-[10px] uppercase tracking-widest text-neutral-500">
              <span className="flex items-center gap-1">
                <Clock size={11} /> {r.minutes} min
              </span>
              <span>{r.calories} kcal</span>
              <span className="text-[#C6FF00]">{r.protein_g}g protein</span>
            </div>
          </div>
        </button>
      ))}
    </div>
  );

const ContentDrawer = ({ item, kind, onClose }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-xl flex items-end sm:items-center justify-center p-3 sm:p-4 overflow-y-auto"
      data-testid={`${kind}-drawer`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-2xl bg-[#111] border border-white/10 rounded-3xl overflow-hidden relative my-4">
        <button
          onClick={onClose}
          data-testid={`${kind}-drawer-close`}
          className="absolute top-4 right-4 z-10 text-neutral-400 hover:text-white p-2 rounded-full bg-black/40"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className="relative aspect-[16/9]">
          <img src={item.cover} alt={item.title} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-[#111]/40 to-transparent" />
        </div>

        <div className="p-6 sm:p-8">
          {kind === "story" ? (
            <>
              <span className="px-2 py-1 rounded-full bg-[#C6FF00] text-[#0A0A0A] text-[10px] font-bold uppercase tracking-wider">
                {item.category}
              </span>
              <h2 className="font-heading text-3xl md:text-4xl text-white mt-3 leading-tight">
                {item.title}
              </h2>
              <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-neutral-500 mt-3 mb-6">
                <span>{new Date(item.published_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</span>
                <span>·</span>
                <span>{item.read_minutes} min read</span>
              </div>
              <div className="text-sm text-neutral-300 leading-relaxed whitespace-pre-line">
                {item.body}
              </div>
            </>
          ) : (
            <>
              <h2 className="font-heading text-3xl md:text-4xl text-white leading-tight mb-1">
                {item.title}
              </h2>
              <p className="text-sm text-neutral-400 mb-5">{item.tagline}</p>
              <div className="grid grid-cols-3 gap-3 mb-6">
                <Stat label="Time" value={`${item.minutes}m`} />
                <Stat label="Calories" value={item.calories} />
                <Stat label="Protein" value={`${item.protein_g}g`} accent />
              </div>
              <div className="flex flex-wrap gap-1.5 mb-6">
                {(item.tags || []).map((t) => (
                  <span
                    key={t}
                    className="px-2 py-0.5 rounded-full border border-white/10 text-[10px] uppercase tracking-wider text-neutral-300"
                  >
                    {t}
                  </span>
                ))}
              </div>
              <div>
                <h3 className="font-heading text-xl text-white mb-2">Ingredients</h3>
                <ul className="space-y-1.5 text-sm text-neutral-300 list-disc list-inside mb-6">
                  {(item.ingredients || []).map((ing) => (
                    <li key={ing}>{ing}</li>
                  ))}
                </ul>
                <h3 className="font-heading text-xl text-white mb-2">Steps</h3>
                <ol className="space-y-2 text-sm text-neutral-300 list-decimal list-inside">
                  {(item.steps || []).map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, accent }) => (
  <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-3 text-center">
    <div className={`font-heading text-2xl leading-none ${accent ? "text-[#C6FF00]" : "text-white"}`}>
      {value}
    </div>
    <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500 font-semibold mt-1.5">
      {label}
    </div>
  </div>
);

const Empty = ({ label }) => (
  <div className="text-center py-16 text-sm text-neutral-500">{label}</div>
);

export default Community;
